import config from './config';
import { FacetAppearance } from './types/FacetAppearance';
import { FilterLayout } from './types/FilterLayout';
import type { FacetResolutionStrategy } from './types/FacetResolutionStrategy';

const defaultFilterGroups = {
  0: { name: 'deckers_color_bucket', label: 'Color' },
  1: { name: 'deckers_size', label: 'Size' },
  2: { name: 'deckers_gender', label: 'Gender' },
  3: { name: 'deckers_shoe_surface', label: 'Best for' },
  4: { name: 'deckers_shoe_type', label: 'Shop By Style' },
  5: { name: 'deckers_price', label: 'Price' }
};

const productList = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /**
       * Refinements (filters, sorting) are by default shown
       * in drawer on mobile, and on top of PLP on desktop.
       *
       * Set this to `true` to also use the drawer on desktop.
       */
      refinementsAlwaysInDrawer: false,
      filterLayout: FilterLayout.TopBar as FilterLayout,
      facets: {
        color: {
          appearance: FacetAppearance.Swatches,
          titleStringPath: 'Color'
        },
        size: {
          appearance: FacetAppearance.ToggleButtons,
          titleStringPath: 'Size',
          hintStringPath: 'facets.size.hint'
        },
        'product-type': {
          appearance: FacetAppearance.Checkboxes,
          titleStringPath: 'Product Type'
        }
      },

      facetResolution: {
        strategy: 'searchParams' as FacetResolutionStrategy,
        /** Whether `"/c/"` should be included as a segment in the path
         * resolution. This is only applicable when the strategy is `'path'`. */
        includeCPrefix: true
      },

      productsPerPage: 24,

      sortBy: 'relevancy',

      /**
       * Determines the styles for the filters on Filters Bar and Filters Modal.
       * The default style is checkboxes for all filters, except for color and size or any other
       * filter we add to this config.
       *
       * **IMPORTANT** -- This is general for all filters, so if you add a new filter regardless of the
       * PLP (Search, Category, etc), you need to add it here.
       */
      filtersStyle: { c_color: 'swatches', c_size: 'square' },

      /**
       * Enables the SEO copy header on the product list page.
       */
      showSEOCopyHeader: true,

      /**
       * The default style for the badges on PDP/PLP.
       */
      badges: {
        plp: {
          /**
           * The position of the badge on the product tile.
           *
           * This is used to configure the position of the badge text on the product tile
           * when no `badgeTextDisplayPosition` is provided on Category.
           *
           * @see {@link BadgeTextDisplayPosition} to all available options.
           */
          badgeTextPosition: 'BELOW_PRODUCT_IMAGE'
        }
      },
      /**
       * Search related configuration for the product list.
       *
       * This is used to configure a search product list page.
       *
       * This is not in a separate configuration because the search page takes advantage of other
       * attributes in the product list page configuration.
       */
      search: {
        filterGroups: defaultFilterGroups,
        // An array of sort criterias. This is used to configure the sorting options for the product list.
        // Similar data structure to filterGroups but with a direction of sorting.
        sortCriterias: { 0: { name: '', label: '', direction: 'ascending' } }
      },
      /** Wheter to display the ratings. */
      displayRatings: false,
      /** Whether to hide products that are out of stock. */
      hideOutOfStockProduct: false,
      /** Hide offline color variants. */
      hideOfflineColorVariants: true,
      /** The group name's to sort the filter values. */
      groupNamesToSortFilterValues: [],
      /**
       * Filter group names that should be expanded by default in the sidebar layout.
       * Groups not listed here will start collapsed.
       */
      expandedFilterGroups: [] as Array<string>,
      /** Whether to show the icon on the mobile filter & sort button. */
      showMobileFilterIcon: true,
      /** Whether the mobile filter modal should render below the site header instead of covering the full viewport. */
      mobileFilterBelowHeader: false,
      /**
       * Error handling strategy for broken product swatches.
       *
       * Catches ANY rendering error in the Swatch component including:
       * - Invalid/malformed hex colors (null, empty, bad format).
       * - Malformed product data from backend.
       * - Any other runtime failure.
       *
       * Modes:
       * - `hide`: Removes broken swatches silently (production-safe).
       * - `showError`: Shows red error swatch with "!" icon (debug-friendly).
       *
       * All errors are logged to console with SKU context regardless of mode.
       */
      swatchErrorHandling: {
        default: 'showError' as const,
        prod: 'hide' as const
      }
    }
  },
  'UGG-US': {
    default: {
      filtersStyle: {
        deckers_color_bucket: 'swatches',
        deckers_size: 'square'
      },
      facetResolution: { strategy: 'path' as FacetResolutionStrategy },
      search: {
        filterGroups: {
          0: { name: 'deckers_shoe_type', label: 'Product Type' },
          1: { name: 'deckers_size', label: 'Size' },
          2: { name: 'deckers_color_bucket', label: 'Color' },
          3: { name: 'deckers_material', label: 'Material' },
          4: { name: 'deckers_price', label: 'Price' }
        },
        sortCriterias: {
          0: {
            name: 'deckers_price',
            label: 'Price - Low to High',
            direction: 'ascending'
          },
          1: {
            name: 'deckers_price',
            label: 'Price - High to Low',
            direction: 'descending'
          }
        }
      },
      displayRatings: true,
      groupNamesToSortFilterValues: ['deckers_size']
    }
  },
  'HOKA-US': {
    default: {
      filterLayout: FilterLayout.Sidebar,
      filtersStyle: {
        deckers_color_bucket: 'swatches',
        deckers_size: 'square',
        c_color: 'swatches',
        c_size: 'square'
      },
      facetResolution: { strategy: 'path' as FacetResolutionStrategy },
      search: {
        filterGroups: {
          0: { name: 'deckers_gender', label: 'Gender' },
          1: { name: 'deckers_size', label: 'Size' },
          2: { name: 'deckers_width', label: 'Fit (Width)' },
          3: { name: 'deckers_color_bucket', label: 'Color' },
          4: { name: 'deckers_shoe_surface', label: 'Best For' },
          5: { name: 'deckers_shoe_ride', label: 'Ride (Cushioning)' },
          6: { name: 'deckers_shoe_stability', label: 'Stability' },
          7: { name: 'deckers_heel_toe_drop', label: 'Heel / Toe Offset (MM)' },
          8: { name: 'deckers_price', label: 'Price' }
        },
        sortCriterias: {
          0: {
            name: 'deckers_price',
            label: 'Price - Low to High',
            direction: 'ascending'
          },
          1: {
            name: 'deckers_price',
            label: 'Price - High to Low',
            direction: 'descending'
          }
        }
      },
      displayRatings: true,
      groupNamesToSortFilterValues: ['deckers_size', 'size'],
      expandedFilterGroups: ['c_color', 'c_size', 'c_shoeRide', 'price'],
      showMobileFilterIcon: false,
      mobileFilterBelowHeader: true
    }
  },
  'TEVA-US': {
    default: {
      filterLayout: FilterLayout.TopBar,
      filtersStyle: {
        deckers_color_bucket: 'swatches',
        deckers_size: 'square'
      },
      facetResolution: { strategy: 'path' as FacetResolutionStrategy },
      search: {
        /**
         * Similar to filterGroups, but for sorting, the parameters are similar, but
         * we concatenate with a direction of sorting.
         */
        sortCriterias: {
          0: {
            name: 'deckers_price',
            label: 'Price - Low to High',
            direction: 'ascending'
          },
          1: {
            name: 'deckers_price',
            label: 'Price - High to Low',
            direction: 'descending'
          }
        }
      },
      displayRatings: true,
      groupNamesToSortFilterValues: ['size']
    }
  }
});

export default productList;
